import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../axiosInstance.js';
import TagsInput from './TagsInput.js';
import MyUserContext from '../myUserContext.js';
import ArtEventDropdown from './ArtEventDropdown'; // Import ArtEventDropdown component

const CardEventArtChatMini = ({ art, onFirstTagAdded }) => {
  const myUser = useContext(MyUserContext);

  // For my event.
  const [artEvent, setArtEvent] = useState(null);
  const [myEvent, setMyEvent] = useState(null);
  useEffect(() => {
    if (art.myEventId) {
      axiosInstance
        .get(`/artEvents/${art.myEventId}`)
        .then((response) => {
          setArtEvent(response.data);
          setMyEvent(response.data.event);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [art.myEventId]);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  const [isArtEventDropdownOpen, setIsArtEventDropdownOpen] = useState(false); // State for dropdown visibility

  return (
    <div className="mx-auto max-w-xl">

      {/* Row: [prompt?] [tags] [chat icon] — visually a single pill like the
          paperplane / calendar rows on ArtPage. Tags stay inline-editable
          in both the "no library entry yet" and "already in library" states;
          only the chat icon opens the discussion overlay. The pill itself
          is no longer clickable and no longer highlights on hover — only
          the chat-icon button does. Prompt shows inside the pill when the
          art isn't yet in the user's library. */}
      <div className="flex justify-center">
      <div
        className={myEvent
          ? "flex flex-col items-center gap-1 px-3 py-3 rounded-full"
          : "flex flex-col items-center gap-1"}
        style={myEvent ? { background: 'var(--mantine-color-default)', minHeight: '48px' } : undefined}
      >
        {!art.myEventId && !myEvent && (() => {
          const noun = art.category === 1
            ? (language === "ru" ? "фильм" : "movie")
            : art.category === 2
              ? (language === "ru" ? "игру" : "game")
              : art.category === 3
                ? (language === "ru" ? "книгу" : "book")
                : (language === "ru" ? "объект" : "item");
          const text = language === "ru"
            ? `Выберите тэг, чтобы добавить ${noun} в свою библиотеку`
            : `Select tag to add the ${noun} to your library`;
          return (
            <span className="text-sm text-center" style={{ color: 'var(--mantine-color-dimmed)' }}>{text}</span>
          );
        })()}
        <div className="flex items-center gap-2">
          <TagsInput
            key={myEvent?.id ?? 'new'}
            language={language}
            editable
            flush
            artCategory={art.category}
            tags={myEvent?.eventTags || []}
            onSetGlobalManualTag={(tag) => {
              if (myEvent === null) {
                // First tag ever — event doesn't exist yet. POST /artEvents
                // still takes the name (not id) for now.
                return axiosInstance
                  .post(`/artEvents`, { globalManualTag: tag.name, artId: art.id, type: "like" /*todo: remove once backend is ready, type is legacy*/ })
                  .then((response) => {
                    setMyEvent(response.data.event);
                    if (onFirstTagAdded) onFirstTagAdded(tag.name);
                  })
                  .catch(console.error);
              }
              // Detect todo/inProgress → consumed transition to fire the
              // logbook-hint callback (same moment as first-tag-added).
              const CONSUMED_TRIGGER = new Set(["todo", "inProgress"]);
              const prevGmt = (myEvent.eventTags || []).find(t => [
                "like","veryGood","good","finished","bad","noRating","inProgress","todo"
              ].includes(t.name));
              const consumedNow = prevGmt && CONSUMED_TRIGGER.has(prevGmt.name) && !CONSUMED_TRIGGER.has(tag.name);
              return axiosInstance
                .post(`/artEvents/tag/${myEvent.id}/globalManualTag`, { tagId: tag.id })
                .then((response) => {
                  // Response is the new tag; refetch event or patch state.
                  // Cheapest: patch eventTags in-place — swap old global-manual for new.
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: [
                      ...(prev.eventTags || []).filter(t => ![
                        "like","veryGood","good","finished","bad","noRating","inProgress","todo"
                      ].includes(t.name)),
                      response.data,
                    ],
                  }) : prev);
                  if (consumedNow && onFirstTagAdded) onFirstTagAdded(tag.name);
                })
                .catch(console.error);
            }}
            onAddCustomTag={(name) => {
              if (!myEvent) return;
              return axiosInstance
                .post(`/artEvents/tag/${myEvent.id}/customTag`, { name })
                .then((response) => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: [...(prev.eventTags || []), response.data],
                  }) : prev);
                })
                .catch(console.error);
            }}
            onRemoveCustomTag={(tagId) => {
              if (!myEvent) return;
              return axiosInstance
                .delete(`/artEvents/tag/${myEvent.id}/customTag`, { data: { tagId } })
                .then(() => {
                  setMyEvent(prev => prev ? ({
                    ...prev,
                    eventTags: (prev.eventTags || []).filter(t => t.id !== tagId),
                  }) : prev);
                })
                .catch(console.error);
            }}
          />
          {myEvent && (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsArtEventDropdownOpen(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsArtEventDropdownOpen(true); }}
              className="rounded-full p-1 cursor-pointer transition-colors inline-flex items-center justify-center"
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              title={language === "ru" ? "Перейти к моему чату" : "Go to my discussion"}
              aria-label={language === "ru" ? "Перейти к моему чату" : "Go to my discussion"}
            >
              <img
                src="/chat_icon.png"
                alt=""
                className="w-6 h-6"
                style={{ filter: 'var(--icon-filter)' }}
              />
            </div>
          )}
        </div>
      </div>
      </div>
      {isArtEventDropdownOpen && (
        <ArtEventDropdown
          eventId={myEvent.id} // Pass myEvent.id explicitly
          onClose={() => setIsArtEventDropdownOpen(false)} // Close dropdown handler
          language={language}
          eventType="artEvent"
          initialArtEvent={artEvent}
          art={art}
          // Keep the first-row tag pill in sync with edits made inside
          // the overlay — without this, the overlay updates only its
          // local state and the outer row (and next overlay open, which
          // re-seeds from initialArtEvent) shows the stale tag.
          onEventUpdated={(updatedEvent) => {
            setMyEvent(updatedEvent);
            setArtEvent((prev) => (prev ? { ...prev, event: updatedEvent } : prev));
          }}
        />
      )}
    </div>
  );
};

export default CardEventArtChatMini;